import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, Phone, Trash2, Loader2, MessageSquare, User, Inbox, Send } from 'lucide-react';

const ManageInquiries = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        fetchMessages();
    }, []);

    const fetchMessages = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/viewmessages`, {
                headers: { authorization: `Bearer ${token}` }  
            });
            setMessages(res.data.messages);
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const deleteMessage = async (id) => {
        if (!window.confirm("Are you sure you want to delete this inquiry?")) return;
        setDeletingId(id);
        try {
            const token = localStorage.getItem('adminToken');
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/deletemessage/${id}`, {
                headers: { authorization: `Bearer ${token}` }
            });
            setMessages(messages.filter(msg => msg._id !== id));
        } catch (err) {
            console.error("Delete error:", err);
            alert("Failed to delete inquiry");
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) return (
        <div className="h-96 flex flex-col items-center justify-center">
            <Loader2 className="animate-spin text-orange-500 mb-4" size={40} />
            <p className="text-slate-400 font-bold tracking-widest uppercase text-[10px]">Opening Inbox...</p>
        </div>
    );

    return (
        <div className="p-4 md:p-8">
            <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-black uppercase italic leading-none">
                    Customer <span className="text-orange-500">Inquiries</span>
                </h1>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2">
                    {messages.length} Direct Messages
                </p>
            </div>

            {/* --- DESKTOP TABLE --- */}
            <div className="hidden lg:block bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-900 text-white text-[10px] uppercase tracking-[0.2em]">
                        <tr>
                            <th className="p-5">SL</th>
                            <th className="p-5">Customer Info</th>
                            <th className="p-5">Message Body</th>
                            <th className="p-5 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm font-medium text-slate-700 divide-y divide-slate-50">
                        {messages.map((msg, index) => (
                            <tr key={msg._id} className="hover:bg-slate-50/50 transition-all group">
                                <td className="p-5 text-slate-300 font-black italic">
                                    {String(index + 1).padStart(2, '0')}
                                </td>
                                <td className="p-5">
                                    <div className="flex flex-col gap-1">
                                        <span className="font-black text-slate-900 uppercase tracking-tight text-xs">{msg.customerName}</span>
                                        <div className="flex flex-col gap-0.5">
                                            <div className="flex items-center gap-2 text-slate-400 text-[11px]">
                                                <Mail size={12} /> {msg.customerEmail}
                                            </div>
                                            <div className="flex items-center gap-2 text-slate-600 font-bold text-[11px]">
                                                <Phone size={12} className="text-orange-500" /> {msg.customerPhone}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-5 max-w-md">
                                    <div className="flex gap-3 bg-slate-50 p-3 rounded-2xl border border-slate-100">
                                        <MessageSquare size={14} className="text-orange-500 mt-1 flex-shrink-0" />
                                        <p className="text-slate-600 leading-relaxed italic text-[12px]">
                                            "{msg.message}"
                                        </p>
                                    </div>
                                </td>
                                <td className="p-5">
                                    <div className="flex items-center justify-center">
                                        <button 
                                            disabled={deletingId === msg._id}
                                            onClick={() => deleteMessage(msg._id)}
                                            className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all disabled:opacity-50"
                                        >
                                            {deletingId === msg._id ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* --- MOBILE CARD VIEW --- */}
            <div className="lg:hidden space-y-4">
                {messages.map((msg, index) => (
                    <div key={msg._id} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
                        <div className="flex justify-between items-center mb-4">
                            <span className="text-[10px] font-black text-slate-300 uppercase italic">Inquiry #{index + 1}</span>
                            <div className="p-2 bg-orange-50 rounded-lg text-orange-500">
                                <Send size={14} />
                            </div>
                        </div>

                        <div className="space-y-2 mb-4">
                            <div className="flex items-center gap-3">
                                <User size={14} className="text-slate-400" />
                                <span className="text-xs font-black uppercase text-slate-900 tracking-tight">{msg.customerName}</span>
                            </div>
                            <div className="flex items-center gap-3 text-slate-500">
                                <Phone size={14} className="text-orange-500" />
                                <span className="text-xs font-bold">{msg.customerPhone}</span>
                            </div>
                        </div>

                        <div className="bg-slate-50 p-4 rounded-xl mb-5 border-l-2 border-orange-500">
                            <p className="text-xs text-slate-600 italic leading-relaxed">
                                {msg.message}
                            </p>
                        </div>

                        <div className="flex gap-2">
                            <a 
                                href={`mailto:${msg.customerEmail}`}
                                className="flex-1 flex items-center justify-center gap-2 bg-slate-900 text-white py-3 rounded-xl text-[10px] font-black uppercase tracking-widest"
                            >
                                <Mail size={14} /> Reply
                            </a>
                            <button 
                                onClick={() => deleteMessage(msg._id)}
                                disabled={deletingId === msg._id}
                                className="w-14 flex items-center justify-center bg-red-50 text-red-500 rounded-xl transition-all disabled:opacity-50"
                            >
                                {deletingId === msg._id ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {messages.length === 0 && (
                <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-slate-100">
                    <Inbox className="mx-auto text-slate-100 mb-4" size={64} />
                    <p className="text-slate-400 font-bold uppercase tracking-[0.2em] text-[10px]">No new messages</p>
                </div>
            )}
        </div>
    );
};

export default ManageInquiries;